import Phaser from 'phaser';
import type { GameDefinition, Scene, Sequence } from '../types/game';
import type { InteractionView } from '../engine/GameEngine';

const BASE_W = 1280;
const BASE_H = 720;

/* Profundidades de capa (orden de pintado). */
const DEPTH = {
  bg: 0,
  tint: 1,
  ambiance: 6,
  light: 8,
  shadow: 9,
  halo: 9,
  markers: 11,
  labels: 12,
  vignette: 60,
  fade: 100
};

type Ambiance = NonNullable<Scene['ambiance']>;

export interface PhaserViewCallbacks {
  onInteract: (id: string) => void;
  onBlocked: (hint: string) => void;
  onSequenceSfx?: (sound: string, volume?: number) => void;
}

interface SceneRefs {
  bg: Phaser.GameObjects.Image | null;
  tint: Phaser.GameObjects.Rectangle | null;
  light: Phaser.GameObjects.Rectangle | null;
  emitter: Phaser.GameObjects.Particles.ParticleEmitter | null;
  markers: Phaser.GameObjects.Container | null;
  vignette: Phaser.GameObjects.Image | null;
  fade: Phaser.GameObjects.Rectangle | null;
  godRays: Phaser.GameObjects.Container | null;
  letterboxTop: Phaser.GameObjects.Rectangle | null;
  letterboxBottom: Phaser.GameObjects.Rectangle | null;
}

/**
 * Crea y gestiona la escena Phaser: fondo con efecto Ken Burns, capas de
 * atmósfera (tinte, luz por hora del día, partículas ambientales y viñeta),
 * personajes con reposo animado y zonas interactivas con halo luminoso.
 *
 * Todo lo decorativo se deriva de campos **opcionales y declarativos** del JSON
 * (`ambiance`, `parallax`, `tint`, `timeOfDay`); la escena nunca contiene lógica
 * de juego. La capa HTML expone además una lista accesible de las mismas
 * interacciones, por lo que el lienzo se marca como decorativo.
 */
export class PhaserView {
  private game: Phaser.Game | null = null;
  private sceneInstance: Phaser.Scene | null = null;
  private refs: SceneRefs = {
    bg: null, tint: null, light: null, emitter: null, markers: null, vignette: null, fade: null, godRays: null,
    letterboxTop: null, letterboxBottom: null
  };
  private reducedMotion = false;
  private currentSceneId: string | null = null;
  private pendingInteractions: InteractionView[] = [];
  private renderedBgScene: string | null = null;
  private liveTweens: Phaser.Tweens.Tween[] = [];
  private sceneObjectSprites: Map<string, Phaser.GameObjects.Image> = new Map();
  private pendingObjectStates: Record<string, string> = {};
  private characterSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private createdAnimations: Set<string> = new Set();

  // Parallax de ratón
  private mouseNorm = { x: 0, y: 0 }; // posición normalizada [-1, 1]
  private mouseTarget = { x: 0, y: 0 };
  private markerBasePositions: Map<Phaser.GameObjects.GameObject, { x: number; y: number; depth: number }> = new Map();

  constructor(
    private readonly parent: HTMLElement,
    private readonly def: GameDefinition,
    private readonly callbacks: PhaserViewCallbacks,
    private readonly assetBaseUrl = ''
  ) {}

  create(): void {
    const self = this;
    const images = this.def.assets.images ?? {};

    class AdventureScene extends Phaser.Scene {
      constructor() {
        super('adventure');
      }
      preload(): void {
        for (const [key, entry] of Object.entries(images)) {
          const src = entry.src.startsWith('data:') ? entry.src : self.assetBaseUrl + entry.src;
          if (entry.kind === 'spritesheet' && entry.frameWidth && entry.frameHeight) {
            this.load.spritesheet(key, src, {
              frameWidth: entry.frameWidth,
              frameHeight: entry.frameHeight
            });
          } else {
            this.load.image(key, src);
          }
        }
        this.load.on('complete', () => {
          self.sceneInstance = this;
        });
      }
      create(): void {
        self.sceneInstance = this;
        self.createProceduralTextures(this);
        self.refs.bg = null;
        self.refs.markers = this.add.container(0, 0).setDepth(DEPTH.markers);
        self.createOverlays(this);
        self.setupMouseParallax(this);
        if (self.currentSceneId) self.renderScene(self.currentSceneId);
      }
    }

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.parent,
      width: BASE_W,
      height: BASE_H,
      backgroundColor: '#05070f',
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: BASE_W,
        height: BASE_H
      },
      scene: [AdventureScene]
    });
    this.parent.setAttribute('aria-hidden', 'true');
  }

  /** Texturas generadas por código (partículas suaves, gotas y viñeta). */
  private createProceduralTextures(scene: Phaser.Scene): void {
    if (!scene.textures.exists('ae-soft')) {
      const g = scene.add.graphics();
      for (let r = 12; r > 0; r--) {
        g.fillStyle(0xffffff, 0.07);
        g.fillCircle(12, 12, r);
      }
      g.fillStyle(0xffffff, 0.55);
      g.fillCircle(12, 12, 3.2);
      g.generateTexture('ae-soft', 24, 24);
      g.destroy();
    }
    if (!scene.textures.exists('ae-drop')) {
      const g = scene.add.graphics();
      g.fillStyle(0xffffff, 0.95);
      g.fillRoundedRect(1, 0, 2, 15, 1);
      g.generateTexture('ae-drop', 4, 15);
      g.destroy();
    }
    if (!scene.textures.exists('ae-vignette')) {
      const tex = scene.textures.createCanvas('ae-vignette', BASE_W, BASE_H);
      if (tex) {
        const ctx = tex.getContext();
        const grad = ctx.createRadialGradient(
          BASE_W / 2, BASE_H / 2, BASE_H * 0.26,
          BASE_W / 2, BASE_H / 2, BASE_H * 0.92
        );
        grad.addColorStop(0, 'rgba(0,0,0,0)');
        grad.addColorStop(0.6, 'rgba(0,0,0,0.12)');
        grad.addColorStop(1, 'rgba(0,0,0,0.62)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, BASE_W, BASE_H);
        tex.refresh();
      }
    }
  }

  /** Viñeta y rectángulo de fundido (persistentes). */
  private createOverlays(scene: Phaser.Scene): void {
    if (scene.textures.exists('ae-vignette')) {
      this.refs.vignette = scene.add
        .image(BASE_W / 2, BASE_H / 2, 'ae-vignette')
        .setDepth(DEPTH.vignette)
        .setAlpha(0.9);
    }
    this.refs.fade = scene.add
      .rectangle(BASE_W / 2, BASE_H / 2, BASE_W, BASE_H, 0x04060d, 1)
      .setDepth(DEPTH.fade);
  }

  /** Configura el seguimiento del ratón para parallax con profundidad. */
  private setupMouseParallax(scene: Phaser.Scene): void {
    scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Normalizar a [-1, 1] desde el centro del canvas
      this.mouseTarget.x = (pointer.x / BASE_W) * 2 - 1;
      this.mouseTarget.y = (pointer.y / BASE_H) * 2 - 1;
    });

    // Actualizar en cada frame (lerp suave)
    scene.events.on('update', () => {
      if (this.reducedMotion) return;

      // Interpolar posición del ratón (factor 0.08 para movimiento fluido)
      this.mouseNorm.x += (this.mouseTarget.x - this.mouseNorm.x) * 0.08;
      this.mouseNorm.y += (this.mouseTarget.y - this.mouseNorm.y) * 0.08;

      // Aplicar offset a marcadores según profundidad
      const parallaxFactor = this.def.scenes[this.currentSceneId ?? '']?.parallax ?? 0;
      for (const [obj, base] of this.markerBasePositions) {
        const offset = base.depth * parallaxFactor;
        const newX = base.x + this.mouseNorm.x * offset;
        const newY = base.y + this.mouseNorm.y * offset * 0.5; // Y se mueve menos
        (obj as Phaser.GameObjects.GameObject & { setPosition(x: number, y: number): void }).setPosition(newX, newY);
      }

      // Aplicar offset sutil al fondo (amplifica Ken Burns)
      const bg = this.refs.bg;
      if (bg) {
        const bgOffset = 12 * parallaxFactor;
        const bgBaseX = BASE_W / 2;
        const bgBaseY = BASE_H / 2;
        bg.setPosition(
          bgBaseX + this.mouseNorm.x * bgOffset,
          bgBaseY + this.mouseNorm.y * bgOffset * 0.5
        );
      }
    });
  }

  /** Muestra un escenario y sus interacciones. */
  showScene(sceneId: string, interactions: InteractionView[]): void {
    const isTransition = this.currentSceneId !== null && this.currentSceneId !== sceneId;
    this.currentSceneId = sceneId;
    this.pendingInteractions = interactions;
    if (this.sceneInstance) {
      if (isTransition && !this.reducedMotion) {
        this.playSceneTransition(sceneId);
      } else {
        this.renderScene(sceneId);
      }
    }
  }

  private renderScene(sceneId: string): void {
    const scene = this.sceneInstance;
    if (!scene) return;
    const sceneDef = this.def.scenes[sceneId];
    if (!sceneDef) return;

    // Mata los tweens decorativos anteriores (se recrean a continuación).
    this.clearTweens();

    // Resetear cámara a posición por defecto.
    const cam = scene.cameras.main;
    cam.setZoom(1);
    cam.scrollX = 0;
    cam.scrollY = 0;

    const sceneChanged = this.renderedBgScene !== sceneId;
    if (sceneChanged) {
      this.buildBackground(scene, sceneDef);
      this.buildAtmosphere(scene, sceneDef);
      this.renderedBgScene = sceneId;
      this.playFadeIn(scene);
    }

    this.applyKenBurns(scene, sceneDef);
    this.buildSceneObjects(scene, sceneDef);
    this.buildCharacterSprites(scene, sceneDef);
    this.buildMarkers(scene, sceneDef);
  }

  /* ----------------------------- fondo / capas ---------------------------- */

  private buildBackground(scene: Phaser.Scene, sceneDef: Scene): void {
    if (this.refs.bg) this.refs.bg.destroy();
    if (scene.textures.exists(sceneDef.background)) {
      // Ligeramente sobredimensionado para que el Ken Burns no descubra bordes.
      this.refs.bg = scene.add
        .image(BASE_W / 2, BASE_H / 2, sceneDef.background)
        .setDisplaySize(BASE_W * 1.06, BASE_H * 1.06)
        .setDepth(DEPTH.bg);
    } else {
      this.refs.bg = null;
    }
  }

  private buildAtmosphere(scene: Phaser.Scene, sceneDef: Scene): void {
    if (this.refs.tint) { this.refs.tint.destroy(); this.refs.tint = null; }
    if (this.refs.light) { this.refs.light.destroy(); this.refs.light = null; }
    if (this.refs.emitter) { this.refs.emitter.destroy(); this.refs.emitter = null; }
    if (this.refs.godRays) { this.refs.godRays.destroy(); this.refs.godRays = null; }

    // Tinte de color declarado en el JSON.
    if (sceneDef.tint) {
      const color = Phaser.Display.Color.HexStringToColor(sceneDef.tint).color;
      this.refs.tint = scene.add
        .rectangle(BASE_W / 2, BASE_H / 2, BASE_W, BASE_H, color, 0.22)
        .setBlendMode(Phaser.BlendModes.MULTIPLY)
        .setDepth(DEPTH.tint);
    }

    // Luz ambiental según la hora del día.
    const light = this.lightConfig(sceneDef.timeOfDay);
    if (light) {
      this.refs.light = scene.add
        .rectangle(BASE_W / 2, BASE_H / 2, BASE_W, BASE_H, light.color, light.alpha)
        .setBlendMode(light.blend)
        .setDepth(DEPTH.light);
    }

    // God rays para dusk/dawn.
    if ((sceneDef.timeOfDay === 'dusk' || sceneDef.timeOfDay === 'dawn') && !this.reducedMotion) {
      this.refs.godRays = this.buildGodRays(scene, sceneDef.timeOfDay);
    }

    // Partículas ambientales.
    if (sceneDef.ambiance && sceneDef.ambiance !== 'none' && !this.reducedMotion) {
      this.refs.emitter = this.buildAmbiance(scene, sceneDef.ambiance);
    }
  }

  private lightConfig(
    time?: Scene['timeOfDay']
  ): { color: number; alpha: number; blend: Phaser.BlendModes } | null {
    switch (time) {
      case 'dawn':
        return { color: 0xffb98a, alpha: 0.14, blend: Phaser.BlendModes.SCREEN };
      case 'dusk':
        return { color: 0xff8a4c, alpha: 0.18, blend: Phaser.BlendModes.MULTIPLY };
      case 'night':
        return { color: 0x152a63, alpha: 0.42, blend: Phaser.BlendModes.MULTIPLY };
      case 'day':
      default:
        return null;
    }
  }

  /** Rayos de luz diagonales para dusk/dawn (origen esquina superior derecha). */
  private buildGodRays(scene: Phaser.Scene, timeOfDay: 'dusk' | 'dawn'): Phaser.GameObjects.Container {
    const container = scene.add.container(0, 0).setDepth(DEPTH.light + 1);
    const rayCount = 4;
    const rays: Phaser.GameObjects.Rectangle[] = [];

    for (let i = 0; i < rayCount; i++) {
      // Rectángulo alargado con alpha bajo
      const rayWidth = 120 + Math.random() * 80;
      const rayHeight = BASE_H * 1.4;
      const alpha = 0.06 + Math.random() * 0.03;
      const ray = scene.add.rectangle(
        BASE_W * 0.75 + i * 60 - 90,
        BASE_H * 0.3,
        rayWidth,
        rayHeight,
        timeOfDay === 'dusk' ? 0xffb347 : 0xffd89b,
        alpha
      );
      ray.setBlendMode(Phaser.BlendModes.SCREEN);
      ray.setRotation(Phaser.Math.DegToRad(20 + i * 3));
      container.add(ray);
      rays.push(ray);
    }

    // Animación sutil: rotación oscilante + pulse de alpha
    if (!this.reducedMotion) {
      const t = scene.tweens.add({
        targets: rays,
        rotation: { from: Phaser.Math.DegToRad(17), to: Phaser.Math.DegToRad(23) },
        alpha: { from: 0.04, to: 0.08 },
        duration: 8000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        stagger: 400
      });
      this.liveTweens.push(t);
    }

    return container;
  }

  private applyKenBurns(scene: Phaser.Scene, sceneDef: Scene): void {
    const bg = this.refs.bg;
    if (!bg) return;
    const p = sceneDef.parallax ?? 0;
    bg.setScale(bg.scaleX, bg.scaleY);
    if (p <= 0 || this.reducedMotion) return;
    const baseScale = bg.scaleX;
    const drift = 18 * p;
    const t = scene.tweens.add({
      targets: bg,
      scale: baseScale * (1 + 0.05 * p),
      x: bg.x + drift,
      y: bg.y + drift * 0.5,
      duration: 16000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    this.liveTweens.push(t);
  }

  private playFadeIn(scene: Phaser.Scene): void {
    const fade = this.refs.fade;
    if (!fade) return;
    if (this.reducedMotion) { fade.setAlpha(0); return; }
    fade.setAlpha(1);
    const t = scene.tweens.add({
      targets: fade,
      alpha: 0,
      duration: 420,
      ease: 'Sine.easeOut'
    });
    this.liveTweens.push(t);
  }

  /** Transición entre escenas según el campo `transition` de la escena destino. */
  private playSceneTransition(newSceneId: string): void {
    const scene = this.sceneInstance;
    const bg = this.refs.bg;
    const fade = this.refs.fade;
    if (!scene || !fade) {
      this.renderScene(newSceneId);
      return;
    }

    const transition = this.def.scenes[newSceneId]?.transition ?? 'zoom';

    switch (transition) {
      case 'fade':
        this.transitionFade(scene, bg, fade, newSceneId);
        break;
      case 'slide-left':
        this.transitionSlide(scene, bg, fade, newSceneId, -1);
        break;
      case 'slide-right':
        this.transitionSlide(scene, bg, fade, newSceneId, 1);
        break;
      case 'zoom':
      default:
        this.transitionZoom(scene, bg, fade, newSceneId);
        break;
    }
  }

  /** Fade a negro, reconstruir escena, fade desde negro. */
  private transitionFade(
    scene: Phaser.Scene, _bg: Phaser.GameObjects.Image | null,
    fade: Phaser.GameObjects.Rectangle, newSceneId: string
  ): void {
    const tweens: Phaser.Tweens.Tween[] = [];
    const t1 = scene.tweens.add({
      targets: fade, alpha: 1, duration: 350, ease: 'Sine.easeIn'
    });
    tweens.push(t1);

    scene.time.delayedCall(380, () => {
      this.renderScene(newSceneId);
      const fadeNew = this.refs.fade;
      if (fadeNew) {
        fadeNew.setAlpha(1);
        const t = scene.tweens.add({
          targets: fadeNew, alpha: 0, duration: 400, ease: 'Sine.easeOut'
        });
        this.liveTweens.push(t);
      }
    });

    for (const t of tweens) this.liveTweens.push(t);
  }

  /** Zoom-out + fade a negro -> reconstruir -> zoom-in + fade desde negro. */
  private transitionZoom(
    scene: Phaser.Scene, bg: Phaser.GameObjects.Image | null,
    fade: Phaser.GameObjects.Rectangle, newSceneId: string
  ): void {
    const tweens: Phaser.Tweens.Tween[] = [];
    if (bg) {
      const t1 = scene.tweens.add({
        targets: bg,
        scaleX: bg.scaleX * 0.92, scaleY: bg.scaleY * 0.92,
        duration: 350, ease: 'Sine.easeIn'
      });
      tweens.push(t1);
    }
    const t2 = scene.tweens.add({
      targets: fade, alpha: 1, duration: 350, ease: 'Sine.easeIn'
    });
    tweens.push(t2);

    scene.time.delayedCall(400, () => {
      this.renderScene(newSceneId);
      const bgNew = this.refs.bg;
      const fadeNew = this.refs.fade;
      if (bgNew) {
        bgNew.setScale(bgNew.scaleX * 1.08, bgNew.scaleY * 1.08);
        const t3 = scene.tweens.add({
          targets: bgNew,
          scaleX: bgNew.scaleX / 1.08, scaleY: bgNew.scaleY / 1.08,
          duration: 400, ease: 'Sine.easeOut'
        });
        this.liveTweens.push(t3);
      }
      if (fadeNew) {
        fadeNew.setAlpha(1);
        const t4 = scene.tweens.add({
          targets: fadeNew, alpha: 0, duration: 400, ease: 'Sine.easeOut'
        });
        this.liveTweens.push(t4);
      }
    });

    for (const t of tweens) this.liveTweens.push(t);
  }

  /** Deslizamiento horizontal: la escena sale hacia un lado y la nueva entra desde el otro. */
  private transitionSlide(
    scene: Phaser.Scene, bg: Phaser.GameObjects.Image | null,
    _fade: Phaser.GameObjects.Rectangle, newSceneId: string, direction: 1 | -1
  ): void {
    const slideOut = BASE_W * direction;
    const slideIn = -BASE_W * direction;

    const targets: Array<Phaser.GameObjects.Image | Phaser.GameObjects.Container | null> = [bg];
    if (this.refs.markers) targets.push(this.refs.markers);

    const t1 = scene.tweens.add({
      targets: targets.filter(Boolean) as Phaser.GameObjects.GameObject[],
      x: slideOut,
      duration: 380,
      ease: 'Sine.easeIn'
    });
    this.liveTweens.push(t1);

    scene.time.delayedCall(400, () => {
      this.renderScene(newSceneId);
      const bgNew = this.refs.bg;
      const markersNew = this.refs.markers;
      const newTargets: Phaser.GameObjects.GameObject[] = [];
      if (bgNew) { bgNew.x = slideIn; newTargets.push(bgNew); }
      if (markersNew) { markersNew.x = slideIn; newTargets.push(markersNew); }

      if (newTargets.length > 0) {
        const t = scene.tweens.add({
          targets: newTargets,
          x: 0,
          duration: 400,
          ease: 'Sine.easeOut'
        });
        this.liveTweens.push(t);
      }
    });
  }

  /* ------------------------------- partículas ----------------------------- */

  private buildAmbiance(
    scene: Phaser.Scene,
    kind: Ambiance
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    const zone = (x: number, y: number, w: number, h: number) => ({
      type: 'random' as const,
      source: new Phaser.Geom.Rectangle(x, y, w, h)
    }) as unknown as Phaser.Types.GameObjects.Particles.EmitZoneData;
    const top = zone(0, -30, BASE_W, 40);
    const full = zone(0, 0, BASE_W, BASE_H);
    const lower = zone(0, BASE_H * 0.35, BASE_W, BASE_H * 0.65);
    const bottom = zone(0, BASE_H * 0.7, BASE_W, BASE_H * 0.4);

    let cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;
    let texture = 'ae-soft';

    switch (kind) {
      case 'rain':
        texture = 'ae-drop';
        cfg = {
          emitZone: top, lifespan: { min: 700, max: 1100 },
          speedY: { min: 950, max: 1350 }, speedX: { min: -70, max: 10 },
          scale: { min: 0.7, max: 1.2 }, alpha: { start: 0.45, end: 0.12 },
          tint: 0xc6dcff, blendMode: Phaser.BlendModes.SCREEN, frequency: 30, quantity: 4
        };
        break;
      case 'snow':
        cfg = {
          emitZone: top, lifespan: { min: 6500, max: 11000 },
          speedY: { min: 30, max: 75 }, speedX: { min: -28, max: 28 },
          scale: { min: 0.22, max: 0.6 }, alpha: { start: 0.85, end: 0.2 },
          tint: 0xffffff, blendMode: Phaser.BlendModes.NORMAL, frequency: 220, quantity: 1
        };
        break;
      case 'leaves':
        cfg = {
          emitZone: top, lifespan: { min: 6000, max: 10000 },
          speedY: { min: 45, max: 95 }, speedX: { min: -45, max: 45 },
          rotate: { min: 0, max: 360 }, scale: { start: 0.5, end: 0.28 },
          alpha: { start: 0.85, end: 0.25 }, tint: [0x9bc46a, 0xd9a441, 0xc9743a],
          blendMode: Phaser.BlendModes.NORMAL, frequency: 380, quantity: 1
        };
        break;
      case 'fireflies':
        cfg = {
          emitZone: lower, lifespan: { min: 3000, max: 6000 },
          speed: { min: 8, max: 32 }, angle: { min: 0, max: 360 },
          scale: { min: 0.28, max: 0.5 }, alpha: { start: 0.95, end: 0.05 },
          tint: 0xd8ff7a, blendMode: Phaser.BlendModes.ADD, frequency: 480, quantity: 1
        };
        break;
      case 'bubbles':
        cfg = {
          emitZone: bottom, lifespan: { min: 3000, max: 6000 },
          speedY: { min: -70, max: -25 }, speedX: { min: -18, max: 18 },
          scale: { start: 0.42, end: 0.14 }, alpha: { start: 0.5, end: 0 },
          tint: 0x9fe8ff, blendMode: Phaser.BlendModes.ADD, frequency: 260, quantity: 1
        };
        break;
      case 'embers':
        cfg = {
          emitZone: bottom, lifespan: { min: 2000, max: 4000 },
          speedY: { min: -130, max: -45 }, speedX: { min: -32, max: 32 },
          scale: { start: 0.4, end: 0.05 }, alpha: { start: 0.95, end: 0 },
          tint: [0xffb347, 0xff7b3d, 0xffe08a], blendMode: Phaser.BlendModes.ADD,
          frequency: 200, quantity: 1
        };
        break;
      case 'mist':
        cfg = {
          emitZone: full, lifespan: { min: 8000, max: 14000 },
          speedX: { min: 6, max: 22 }, speedY: { min: -4, max: 4 },
          scale: { start: 2.4, end: 3.4 }, alpha: { start: 0.1, end: 0 },
          tint: 0xcfe0ff, blendMode: Phaser.BlendModes.SCREEN, frequency: 850, quantity: 1
        };
        break;
      case 'pollen':
        cfg = {
          emitZone: full, lifespan: { min: 6000, max: 11000 },
          speed: { min: 5, max: 20 }, angle: { min: 0, max: 360 },
          scale: { min: 0.22, max: 0.5 }, alpha: { start: 0.55, end: 0 },
          tint: 0xffe9a8, blendMode: Phaser.BlendModes.ADD, frequency: 260, quantity: 1
        };
        break;
      case 'dust':
      default:
        cfg = {
          emitZone: full, lifespan: { min: 6000, max: 11000 },
          speed: { min: 4, max: 18 }, angle: { min: 0, max: 360 },
          scale: { min: 0.2, max: 0.5 }, alpha: { start: 0.5, end: 0 },
          tint: 0xdfe9ff, blendMode: Phaser.BlendModes.ADD, frequency: 300, quantity: 1
        };
        break;
    }

    const emitter = scene.add.particles(0, 0, texture, cfg).setDepth(DEPTH.ambiance);
    return emitter;
  }

  /* -------------------------------- objetos de escena --------------------- */

  private buildSceneObjects(scene: Phaser.Scene, sceneDef: Scene): void {
    for (const sprite of this.sceneObjectSprites.values()) sprite.destroy();
    this.sceneObjectSprites.clear();

    const objects = sceneDef.objects;
    if (!objects) return;

    for (const [objId, obj] of Object.entries(objects)) {
      const stateKey = this.pendingObjectStates[objId] ?? obj.initialState;
      const stateDef = obj.states[stateKey];
      if (!stateDef || !scene.textures.exists(stateDef.image)) continue;

      const x = stateDef.position.x * BASE_W;
      const y = stateDef.position.y * BASE_H;
      const sprite = scene.add
        .image(x, y, stateDef.image)
        .setDepth(stateDef.depth ?? DEPTH.markers - 1);
      if (stateDef.scale) sprite.setScale(stateDef.scale);
      this.sceneObjectSprites.set(objId, sprite);
    }
  }

  /** Cambia el estado visual de un objeto de escena con crossfade. */
  setObjectState(objectId: string, state: string, sceneDef: Scene): void {
    const obj = sceneDef.objects?.[objectId];
    if (!obj) return;
    const stateDef = obj.states[state];
    if (!stateDef) return;
    const scene = this.sceneInstance;
    if (!scene) return;

    const existing = this.sceneObjectSprites.get(objectId);
    if (!scene.textures.exists(stateDef.image)) return;

    const x = stateDef.position.x * BASE_W;
    const y = stateDef.position.y * BASE_H;
    const depth = stateDef.depth ?? DEPTH.markers - 1;

    if (existing) {
      if (this.reducedMotion) {
        existing.setTexture(stateDef.image);
        existing.setPosition(x, y);
        if (stateDef.scale) existing.setScale(stateDef.scale);
        existing.setDepth(depth);
      } else {
        const newSprite = scene.add
          .image(x, y, stateDef.image)
          .setDepth(depth)
          .setAlpha(0);
        if (stateDef.scale) newSprite.setScale(stateDef.scale);
        const t = scene.tweens.add({
          targets: [existing, newSprite],
          alpha: [0, 1],
          duration: 300,
          ease: 'Sine.easeInOut',
          onComplete: () => { existing.destroy(); }
        });
        this.liveTweens.push(t);
        this.sceneObjectSprites.set(objectId, newSprite);
      }
    } else {
      const sprite = scene.add
        .image(x, y, stateDef.image)
        .setDepth(depth)
        .setAlpha(this.reducedMotion ? 1 : 0);
      if (stateDef.scale) sprite.setScale(stateDef.scale);
      if (!this.reducedMotion) {
        const t = scene.tweens.add({
          targets: sprite, alpha: 1, duration: 300, ease: 'Sine.easeInOut'
        });
        this.liveTweens.push(t);
      }
      this.sceneObjectSprites.set(objectId, sprite);
    }
  }

  /* -------------------------------- personajes en escena --------------------- */

  private charactersWithSprite = new Set<string>();

  private buildCharacterSprites(scene: Phaser.Scene, sceneDef: Scene): void {
    for (const sprite of this.characterSprites.values()) sprite.destroy();
    this.characterSprites.clear();
    this.charactersWithSprite.clear();

    const placements = sceneDef.characters;
    if (!placements) return;

    for (const [placementId, placement] of Object.entries(placements)) {
      const charDef = this.def.characters[placement.character];
      if (!charDef?.spriteSheet) continue;

      const sheetKey = charDef.spriteSheet;
      if (!scene.textures.exists(sheetKey)) continue;

      this.ensureCharacterAnimations(scene, charDef, sheetKey);

      const x = placement.position.x * BASE_W;
      const y = placement.position.y * BASE_H;
      const scale = placement.scale ?? charDef.spriteScale ?? 1;
      const anchor = charDef.spriteAnchor ?? 'center';

      const sprite = scene.add.sprite(x, y, sheetKey);
      sprite.setScale(scale);
      sprite.setDepth(DEPTH.markers - 1);

      if (anchor === 'feet') {
        sprite.setOrigin(0.5, 1);
        sprite.setPosition(x, y);
      }

      const animKey = `${charDef.id}-${placement.animation ?? 'idle'}`;
      if (scene.anims.exists(animKey)) {
        sprite.play(animKey);
      }

      this.characterSprites.set(placementId, sprite);
      this.charactersWithSprite.add(placement.character);
    }
  }

  private ensureCharacterAnimations(
    scene: Phaser.Scene,
    charDef: import('../types/game').Character,
    sheetKey: string
  ): void {
    if (!charDef.animations) return;
    const prefix = `ae-char-${charDef.id}`;
    if (this.createdAnimations.has(prefix)) return;

    for (const [animName, frameKey] of Object.entries(charDef.animations)) {
      const fullKey = `${charDef.id}-${animName}`;
      if (scene.anims.exists(fullKey)) continue;
      scene.anims.create({
        key: fullKey,
        frames: scene.anims.generateFrameNames(sheetKey, {
          prefix: `${frameKey}_`,
          start: 0,
          end: 5
        }),
        frameRate: 8,
        repeat: -1
      });
    }
    this.createdAnimations.add(prefix);
  }

  private hasCharacterSpriteAt(x: number, y: number): boolean {
    for (const sprite of this.characterSprites.values()) {
      const dx = sprite.x - x;
      const dy = sprite.y - y;
      if (Math.sqrt(dx * dx + dy * dy) < 50) return true;
    }
    return false;
  }

  /* -------------------------------- marcadores ---------------------------- */

  private buildMarkers(scene: Phaser.Scene, _sceneDef: Scene): void {
    if (this.refs.markers) this.refs.markers.destroy();
    const container = scene.add.container(0, 0).setDepth(DEPTH.markers);
    this.refs.markers = container;
    this.markerBasePositions.clear();

    for (const inter of this.pendingInteractions) {
      const pos = inter.position ?? this.defaultPosition(inter);
      const x = pos.x * BASE_W;
      const y = pos.y * BASE_H;
      this.buildMarker(scene, container, inter, x, y);
    }
  }

  private defaultPosition(inter: InteractionView): { x: number; y: number } {
    if (inter.area) {
      return { x: inter.area.x + inter.area.w / 2, y: inter.area.y + inter.area.h / 2 };
    }
    return { x: 0.5, y: 0.6 };
  }

  private buildMarker(
    scene: Phaser.Scene,
    container: Phaser.GameObjects.Container,
    inter: InteractionView,
    x: number,
    y: number
  ): void {
    const isCharacter = inter.kind === 'character';
    
    // Check if this character has a sprite rendered (skip halo if so)
    const hasSprite = isCharacter && this.hasCharacterSpriteAt(x, y);
    
    const radius = isCharacter ? 30 : inter.kind === 'exit' ? 26 : 23;
    const color = !inter.enabled
      ? 0x94a3b8
      : inter.kind === 'exit'
        ? 0x5ef2a8
        : isCharacter
          ? 0xffb347
          : 0xffd479;

    // Halo luminoso pulsante (omitido para personajes con sprite)
    if (inter.enabled && !this.reducedMotion && !hasSprite) {
      const haloScale = isCharacter ? 3.2 : 2.6;
      const haloAlpha = isCharacter ? 0.35 : 0.5;
      const halo = scene.add
        .image(x, y, 'ae-soft')
        .setTint(color)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(DEPTH.halo)
        .setScale((radius * haloScale) / 24)
        .setAlpha(haloAlpha);
      container.add(halo);
      const t = scene.tweens.add({
        targets: halo,
        scale: { from: (radius * (haloScale - 0.2)) / 24, to: (radius * (haloScale + 0.8)) / 24 },
        alpha: { from: haloAlpha + 0.05, to: haloAlpha - 0.15 },
        duration: isCharacter ? 2400 : 1700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
      this.liveTweens.push(t);
    }

    // Anillo nítido + punto central (omitido para personajes con sprite).
    const g = scene.add.graphics().setDepth(DEPTH.markers);
    if (!hasSprite) {
      g.fillStyle(color, inter.enabled ? 0.18 : 0.08);
      g.fillCircle(0, 0, radius);
      g.lineStyle(3, color, inter.enabled ? 0.95 : 0.5);
      g.strokeCircle(0, 0, radius);
      g.fillStyle(0xffffff, inter.enabled ? 0.92 : 0.4);
      g.fillCircle(0, 0, 4);
    }
    g.setPosition(x, y);
    g.setInteractive(new Phaser.Geom.Circle(0, 0, radius + 8), Phaser.Geom.Circle.Contains);
    container.add(g);

    // Registrar posición base para parallax (profundidad según tipo)
    const depth = isCharacter ? 18 : inter.kind === 'exit' ? 5 : 8;
    this.markerBasePositions.set(g, { x, y, depth });

    const label = this.makeLabel(scene, x, y - radius - 12, inter.label);
    container.add(label);
    this.wirePointer(scene, g, label, inter);
  }


  private makeLabel(scene: Phaser.Scene, x: number, y: number, text: string): Phaser.GameObjects.Text {
    const label = scene.add
      .text(x, y, text, {
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        fontSize: '16px',
        fontStyle: '600',
        color: '#eaf0ff',
        align: 'center',
        stroke: '#04060d',
        strokeThickness: 4
      })
      .setOrigin(0.5, 1)
      .setResolution(2)
      .setDepth(DEPTH.labels)
      .setAlpha(0)
      .setVisible(false);
    label.setBackgroundColor('rgba(10,15,30,0.72)');
    label.setPadding(6, 5, 6, 5);
    return label;
  }

  private wirePointer(
    scene: Phaser.Scene,
    obj: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics,
    label: Phaser.GameObjects.Text,
    inter: InteractionView
  ): void {
    const showLabel = () => {
      label.setVisible(true);
      scene.tweens.add({ targets: label, alpha: 1, y: label.y - 4, duration: 140, ease: 'Sine.easeOut' });
    };
    const hideLabel = () => {
      scene.tweens.add({
        targets: label,
        alpha: 0,
        duration: 120,
        onComplete: () => label.setVisible(false)
      });
    };
    obj.on('pointerover', () => {
      scene.tweens.add({ targets: obj, scale: (obj.scale || 1) * 1.12, duration: 140, ease: 'Sine.easeOut' });
      showLabel();
      this.parent.style.cursor = 'pointer';
    });
    obj.on('pointerout', () => {
      scene.tweens.add({ targets: obj, scale: 1, duration: 160, ease: 'Sine.easeOut' });
      hideLabel();
      this.parent.style.cursor = '';
    });
    obj.on('pointerdown', () => {
      if (!inter.enabled) {
        this.callbacks.onBlocked(inter.disabledHint ?? 'Aún no puedes interactuar con esto.');
        return;
      }
      const pos = inter.position ?? this.defaultPosition(inter);
      this.playInteractFlash(pos.x, pos.y);
      this.callbacks.onInteract(inter.id);
    });
  }


  /** Ráfaga de partículas doradas en el centro (evidencia recolectada). */
  playEvidenceBurst(): void {
    const scene = this.sceneInstance;
    if (!scene || this.reducedMotion) return;
    const cx = BASE_W / 2;
    const cy = BASE_H / 2;
    const g = scene.add.graphics().setDepth(DEPTH.halo + 1);
    g.fillStyle(0xffd479, 0.9);
    g.fillCircle(0, 0, 6);
    g.generateTexture('ae-burst-particle', 12, 12);
    g.destroy();

    const emitter = scene.add.particles(cx, cy, 'ae-burst-particle', {
      speed: { min: 120, max: 280 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.2, end: 0 },
      alpha: { start: 1, end: 0 },
      tint: [0xffd479, 0xffb347, 0xffffff],
      blendMode: Phaser.BlendModes.ADD,
      lifespan: 600,
      quantity: 18,
      emitting: false
    }).setDepth(DEPTH.halo + 1);

    emitter.explode(18);
    scene.time.delayedCall(700, () => emitter.destroy());
  }

  /** Sacudida breve de la cámara (objetivo completado). */
  playScreenShake(): void {
    const scene = this.sceneInstance;
    if (!scene || this.reducedMotion) return;
    const bg = this.refs.bg;
    const markers = this.refs.markers;
    if (!bg) return;

    const origBgX = bg.x;
    const origBgY = bg.y;
    const origMarkersX = markers?.x ?? 0;
    const origMarkersY = markers?.y ?? 0;

    const targets: Phaser.GameObjects.GameObject[] = [bg];
    if (markers) targets.push(markers);

    let step = 0;
    const shakeCount = 6;
    const intensity = 4;
    const interval = scene.time.addEvent({
      delay: 40,
      repeat: shakeCount,
      callback: () => {
        const dx = (Math.random() - 0.5) * intensity * 2;
        const dy = (Math.random() - 0.5) * intensity * 2;
        const decay = 1 - step / shakeCount;
        bg.setPosition(origBgX + dx * decay, origBgY + dy * decay);
        if (markers) markers.setPosition(origMarkersX + dx * decay, origMarkersY + dy * decay);
        step++;
      }
    });

    scene.time.delayedCall(40 * (shakeCount + 1), () => {
      bg.setPosition(origBgX, origBgY);
      if (markers) markers.setPosition(origMarkersX, origMarkersY);
      interval.destroy();
    });
  }

  /** Flash radial en una posición al interactuar. */
  playInteractFlash(normX: number, normY: number): void {
    const scene = this.sceneInstance;
    if (!scene || this.reducedMotion) return;
    const x = normX * BASE_W;
    const y = normY * BASE_H;

    const flash = scene.add
      .image(x, y, 'ae-soft')
      .setTint(0xffffff)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(DEPTH.halo + 2)
      .setScale(0.5)
      .setAlpha(0.9);

    const t = scene.tweens.add({
      targets: flash,
      scale: 4,
      alpha: 0,
      duration: 350,
      ease: 'Sine.easeOut',
      onComplete: () => flash.destroy()
    });
    this.liveTweens.push(t);
  }

  /** Muestra barras negras superior e inferior (cinematográfico). */
  showLetterbox(): void {
    const scene = this.sceneInstance;
    if (!scene) return;
    const barH = BASE_H * 0.08;

    if (!this.refs.letterboxTop) {
      this.refs.letterboxTop = scene.add
        .rectangle(BASE_W / 2, -barH / 2, BASE_W, barH, 0x04060d, 1)
        .setDepth(DEPTH.vignette - 1);
    }
    if (!this.refs.letterboxBottom) {
      this.refs.letterboxBottom = scene.add
        .rectangle(BASE_W / 2, BASE_H + barH / 2, BASE_W, barH, 0x04060d, 1)
        .setDepth(DEPTH.vignette - 1);
    }

    if (this.reducedMotion) {
      this.refs.letterboxTop.setPosition(BASE_W / 2, barH / 2);
      this.refs.letterboxBottom.setPosition(BASE_W / 2, BASE_H - barH / 2);
      return;
    }

    const t1 = scene.tweens.add({
      targets: this.refs.letterboxTop,
      y: barH / 2,
      duration: 400,
      ease: 'Sine.easeOut'
    });
    const t2 = scene.tweens.add({
      targets: this.refs.letterboxBottom,
      y: BASE_H - barH / 2,
      duration: 400,
      ease: 'Sine.easeOut'
    });
    this.liveTweens.push(t1, t2);
  }

  /** Retira las barras cinematográficas. */
  hideLetterbox(): void {
    const scene = this.sceneInstance;
    if (!scene) return;

    const top = this.refs.letterboxTop;
    const bottom = this.refs.letterboxBottom;

    if (this.reducedMotion || !top || !bottom) {
      top?.destroy();
      bottom?.destroy();
      this.refs.letterboxTop = null;
      this.refs.letterboxBottom = null;
      return;
    }

    const t1 = scene.tweens.add({
      targets: top,
      y: -top.height / 2,
      duration: 300,
      ease: 'Sine.easeIn',
      onComplete: () => { top.destroy(); this.refs.letterboxTop = null; }
    });
    const t2 = scene.tweens.add({
      targets: bottom,
      y: BASE_H + bottom.height / 2,
      duration: 300,
      ease: 'Sine.easeIn',
      onComplete: () => { bottom.destroy(); this.refs.letterboxBottom = null; }
    });
    this.liveTweens.push(t1, t2);
  }

  /** Zoom sutil al abrir diálogo (enfoca la atención). */
  playDialogueZoom(): void {
    const scene = this.sceneInstance;
    const bg = this.refs.bg;
    if (!scene || !bg || this.reducedMotion) return;

    const t = scene.tweens.add({
      targets: bg,
      scaleX: bg.scaleX * 1.04,
      scaleY: bg.scaleY * 1.04,
      duration: 800,
      ease: 'Sine.easeOut',
      yoyo: true,
      hold: 2000
    });
    this.liveTweens.push(t);
  }

  /** Reproduce una secuencia audiovisual paso a paso. Llama onComplete al terminar. */
  playSequence(seq: Sequence, onComplete: () => void): void {
    const scene = this.sceneInstance;
    if (!scene) { onComplete(); return; }

    if (seq.cinematic) this.showLetterbox();

    let elapsed = 0;
    for (const step of seq.steps) {
      const delay = elapsed + (step.delay ?? 0);
      scene.time.delayedCall(delay, () => this.executeSequenceStep(step));
      elapsed = delay + step.duration;
    }

    scene.time.delayedCall(elapsed + 50, () => {
      if (seq.cinematic) this.hideLetterbox();
      onComplete();
    });
  }

  private executeSequenceStep(step: { action: import('../types/game').SequenceAction; duration: number }): void {
    const scene = this.sceneInstance;
    if (!scene) return;
    const a = step.action;

    switch (a.type) {
      case 'cameraMove': {
        const cam = scene.cameras.main;
        const props: Record<string, number | string> = { duration: a.duration, ease: 'Sine.easeInOut' };
        if (a.x !== undefined) props.scrollX = a.x * BASE_W - BASE_W / 2;
        if (a.y !== undefined) props.scrollY = a.y * BASE_H - BASE_H / 2;
        if (a.zoom !== undefined) props.zoom = a.zoom;
        scene.tweens.add({ targets: cam, ...props });
        break;
      }
      case 'showObject': {
        const sceneDef = this.def.scenes[this.currentSceneId ?? ''];
        if (sceneDef) this.setObjectState(a.object, a.state, sceneDef);
        break;
      }
      case 'fadeOverlay': {
        const fade = this.refs.fade;
        if (fade) {
          if (a.color) fade.setFillStyle(Phaser.Display.Color.HexStringToColor(a.color).color, 1);
          scene.tweens.add({ targets: fade, alpha: a.alpha, duration: a.duration, ease: 'Sine.easeInOut' });
        }
        break;
      }
      case 'sfx':
        this.callbacks.onSequenceSfx?.(a.sound, a.volume);
        break;
      case 'text': {
        const px = (a.position?.x ?? 0.5) * BASE_W;
        const py = (a.position?.y ?? 0.85) * BASE_H;
        const style = a.style === 'caption'
          ? { fontFamily: 'monospace', fontSize: '18px', color: '#e0e0e0', align: 'center', wordWrap: { width: BASE_W * 0.7 } }
          : { fontFamily: 'Georgia, serif', fontSize: '22px', color: '#ffffff', align: 'center', fontStyle: 'italic', wordWrap: { width: BASE_W * 0.7 } };
        const txt = scene.add.text(px, py, a.text, style).setOrigin(0.5).setAlpha(0).setDepth(DEPTH.fade - 1);
        const half = Math.max(a.duration * 0.3, 300);
        scene.tweens.add({ targets: txt, alpha: 1, duration: half, ease: 'Sine.easeIn' });
        scene.time.delayedCall(a.duration - half, () => {
          scene.tweens.add({ targets: txt, alpha: 0, duration: half, ease: 'Sine.easeOut', onComplete: () => txt.destroy() });
        });
        break;
      }
      case 'setLight': {
        const lightRect = this.refs.light;
        if (lightRect) {
          const targetAlpha = a.enabled ? (a.intensity ?? 0.6) : 0;
          scene.tweens.add({ targets: lightRect, alpha: targetAlpha, duration: step.duration, ease: 'Sine.easeInOut' });
        }
        break;
      }
      case 'wait':
        break;
    }
  }

  /** Establece los estados actuales de objetos antes de renderizar una escena. */
  setObjectStates(states: Record<string, string>): void {
    this.pendingObjectStates = { ...states };
  }

  /** Zoom de cámara hacia un punto con reverte automático. */
  cameraZoom(target?: { x: number; y: number }, scale = 1.8, duration = 600, hold = 1500): void {
    const scene = this.sceneInstance;
    if (!scene || this.reducedMotion) return;
    const cam = scene.cameras.main;

    const cx = target ? target.x * BASE_W : BASE_W / 2;
    const cy = target ? target.y * BASE_H : BASE_H / 2;

    cam.setZoom(1);
    cam.centerOn(BASE_W / 2, BASE_H / 2);

    const zoomedX = cx - (BASE_W / 2) / scale;
    const zoomedY = cy - (BASE_H / 2) / scale;

    scene.tweens.add({
      targets: cam,
      zoom: scale,
      scrollX: zoomedX,
      scrollY: zoomedY,
      duration,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        scene.time.delayedCall(hold, () => {
          scene.tweens.add({
            targets: cam,
            zoom: 1,
            scrollX: 0,
            scrollY: 0,
            duration: duration * 0.8,
            ease: 'Sine.easeInOut'
          });
        });
      }
    });
  }

  /** Paneo de cámara hacia un punto con reverte automático. */
  cameraPan(to: { x: number; y: number }, duration = 800, hold = 1200): void {
    const scene = this.sceneInstance;
    if (!scene || this.reducedMotion) return;
    const cam = scene.cameras.main;

    const tx = to.x * BASE_W - BASE_W / 2;
    const ty = to.y * BASE_H - BASE_H / 2;

    cam.setZoom(1.3);
    cam.centerOn(BASE_W / 2, BASE_H / 2);

    scene.tweens.add({
      targets: cam,
      scrollX: tx,
      scrollY: ty,
      duration,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        scene.time.delayedCall(hold, () => {
          scene.tweens.add({
            targets: cam,
            scrollX: 0,
            scrollY: 0,
            zoom: 1,
            duration: duration * 0.6,
            ease: 'Sine.easeInOut'
          });
        });
      }
    });
  }

  setReducedMotion(value: boolean): void {
    this.reducedMotion = value;
  }

  private clearTweens(): void {
    for (const t of this.liveTweens) {
      if (t && t.isPlaying()) t.remove();
    }
    this.liveTweens = [];
  }

  destroy(): void {
    this.clearTweens();
    for (const sprite of this.sceneObjectSprites.values()) sprite.destroy();
    this.sceneObjectSprites.clear();
    for (const sprite of this.characterSprites.values()) sprite.destroy();
    this.characterSprites.clear();
    this.charactersWithSprite.clear();
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
    }
    this.sceneInstance = null;
    this.refs = { bg: null, tint: null, light: null, emitter: null, markers: null, vignette: null, fade: null, godRays: null, letterboxTop: null, letterboxBottom: null };
  }
}
